import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { SITE } from "@/config/site";

const LINKS = [
  { label: "About", href: "#about" },
  { label: "Method", href: "#method" },
  { label: "Modules", href: "#modules" },
  { label: "Results", href: "#testimonials" },
  { label: "Pricing", href: "#pricing" },
  { label: "FAQ", href: "#faq" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, delay: 0.2 }}
      data-testid="site-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,padding] duration-500 ${
        scrolled
          ? "bg-ink/85 backdrop-blur-xl border-b border-white/10 py-3"
          : "bg-transparent py-6"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
        <a href="#top" data-testid="nav-logo" className="flex flex-col leading-none">
          <span className="font-display text-2xl md:text-3xl italic text-white">
            {SITE.coach}
          </span>
          <span className="font-mono text-[9px] md:text-[10px] tracking-[0.4em] text-burgundy uppercase">
            {SITE.brand}
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-9">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className="font-mono text-xs uppercase tracking-[0.15em] text-slate-300 hover:text-white transition-colors duration-300"
            >
              {l.label}
            </a>
          ))}
          <a
            href="#enquire"
            data-testid="nav-cta"
            className="bg-burgundy hover:bg-burgundy-hover text-ink font-semibold text-xs font-mono uppercase tracking-[0.15em] px-5 py-2.5 rounded-full transition-colors duration-300"
          >
            Apply Now
          </a>
        </nav>

        <button
          className="md:hidden text-white"
          onClick={() => setOpen((v) => !v)}
          data-testid="nav-mobile-toggle"
          aria-label="Menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden bg-surface border-t border-white/10 mt-3"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  className="font-mono text-sm uppercase tracking-[0.15em] text-slate-200"
                >
                  {l.label}
                </a>
              ))}
              <a
                href="#enquire"
                onClick={() => setOpen(false)}
                className="bg-burgundy text-ink font-semibold text-sm font-mono uppercase tracking-[0.15em] px-5 py-3 rounded-full text-center"
              >
                Apply Now
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
